import * as d3 from 'd3'

export const CHART_HEIGHT = 150
export const CHART_MARGIN = { top: 8, right: 8, bottom: 24, left: 44 }
export const AXIS_TEXT_COLOR = 'var(--color-secondary)'
export const AXIS_LINE_COLOR = 'var(--color-secondary)'
export const AXIS_FONT_SIZE = 12

export type SeriesColor = 'gold' | 'mint'

// Keep in sync with the .bpx-swatch-* legend classes in ../base.css.
export const SERIES_COLORS: Record<SeriesColor, string> = {
  gold: 'var(--color-bpx-chart-gold)',
  mint: 'var(--color-success)',
}

export type ChartGroup = d3.Selection<SVGGElement, unknown, null, undefined>

export interface ChartFrame {
  chart: ChartGroup
  innerWidth: number
  innerHeight: number
}

export function createChartFrame(element: Element): ChartFrame {
  const width = (element as HTMLElement).clientWidth || 320
  const innerWidth = width - CHART_MARGIN.left - CHART_MARGIN.right
  const innerHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom

  const svg = d3
    .select(element)
    .append('svg')
    .attr('width', width)
    .attr('height', CHART_HEIGHT)
    .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)

  const chart = svg
    .append('g')
    .attr('transform', `translate(${CHART_MARGIN.left}, ${CHART_MARGIN.top})`)

  return { chart, innerWidth, innerHeight }
}

export function drawYTickLabel(
  frame: ChartFrame,
  y: number,
  text: string,
): void {
  frame.chart
    .append('text')
    .attr('x', -10)
    .attr('y', y)
    .attr('text-anchor', 'end')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', AXIS_FONT_SIZE)
    .style('fill', AXIS_TEXT_COLOR)
    .text(text)
}

export function drawXTickLabel(
  frame: ChartFrame,
  x: number,
  anchor: 'start' | 'middle' | 'end',
  text: string,
): void {
  frame.chart
    .append('text')
    .attr('x', x)
    .attr('y', frame.innerHeight + 18)
    .attr('text-anchor', anchor)
    .attr('font-size', AXIS_FONT_SIZE)
    .style('fill', AXIS_TEXT_COLOR)
    .text(text)
}

export function drawHorizontalLine(
  frame: ChartFrame,
  y: number,
  options: { dashed?: boolean; opacity?: number } = {},
): void {
  const line = frame.chart
    .append('line')
    .attr('x1', 0)
    .attr('x2', frame.innerWidth)
    .attr('y1', y)
    .attr('y2', y)
    .attr('stroke-width', 1)
    .attr('opacity', options.opacity ?? 1)
    .style('stroke', AXIS_LINE_COLOR)
  if (options.dashed) {
    line.attr('stroke-dasharray', '4 4')
  }
}

export function formatTickValue(value: number): string {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1)
}
